import { useMemo } from 'react';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box';
import { Icon } from 'components/Icon';
import {
  PageHeaderDropdownMenu,
  PageHeaderDropdownMenuItem,
  PageHeaderDropdownMenuSection,
  PageHeaderDropdownMenuSectionTitle,
} from 'components/Page/Header/PageHeaderDropdownMenu';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import {
  NOTIFICATIONS,
  NotificationStatus,
  useLocalNotifications,
} from 'utils/localNotifications';
import { hasResumeDb, promoteResumeDatabase } from 'utils/resumeDatabase';

import { PageHeaderAccountDropdownProfileSection } from './PageHeaderAccountDropdownProfileSection';
import { PageHeaderDropdownConditionalMenuItem } from './PageHeaderDropdownConditionalMenuItem';

const NAV_WIDTH = {
  en: 900,
  es: 1050,
  pt: 1000,
};

type Props = {
  isStaff: boolean;
};

export function PageHeaderAccountDropdown({ isStaff }: Props) {
  const userEnvironment = useUserEnvironment();

  const { user } = userEnvironment;

  const { localNotifications } = useLocalNotifications();

  const resumeNotification = useMemo(
    () =>
      localNotifications?.find(
        (notification) => notification.type === NOTIFICATIONS.UPLOAD_RESUME,
      ),
    [localNotifications],
  );

  const isAdmin = (user?.orgs?.length || 0) > 0;

  const promoteResumeDb = useMemo(
    () =>
      resumeNotification?.status === NotificationStatus.NEW &&
      promoteResumeDatabase({ userEnvironment }),
    [resumeNotification, userEnvironment],
  );

  return (
    <Box position="relative">
      <PageHeaderDropdownMenu
        right
        aria-label={getText('Account Information')}
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/no-unstable-nested-components
        title={() => (
          <Box
            display={['none', null, null, 'flex']}
            alignItems="center"
            maxWidth={[
              null,
              null,
              null,
              `calc(100vw - ${NAV_WIDTH[CURRENT_LOCALE]}px)`,
            ]}
            minWidth={[null, null, null, 32]}
          >
            <Box whiteSpace="noWrap" textOverflow="ellipsis" overflow="hidden">
              {
                /* TRANSLATORS: user greeting at top of page */
                sprintf(getText('Hi, %(name)s!'), {
                  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                  name: user.firstName || user.email,
                })
              }
            </Box>
          </Box>
        )}
        data-qa-id="nav-dropdown"
      >
        {({ closeMenu }) => (
          <>
            {promoteResumeDb ? (
              <PageHeaderAccountDropdownProfileSection closeMenu={closeMenu} />
            ) : null}
            {
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              user.orgs.length > 0 && (
                <PageHeaderDropdownMenuSection $noBorder>
                  <PageHeaderDropdownMenuSectionTitle>
                    {getText('Manage')}
                  </PageHeaderDropdownMenuSectionTitle>
                  {
                    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                    user.orgs.map((org) => (
                      <PageHeaderDropdownConditionalMenuItem
                        key={org.id}
                        isStaff={isStaff}
                        to={
                          org.hasGradProgram && !org.overrideToMainDashboard
                            ? `/admin/${org.id}/grad-program`
                            : `/admin/${org.id}`
                        }
                        onClick={closeMenu}
                      >
                        {org.name}
                      </PageHeaderDropdownConditionalMenuItem>
                    ))
                  }
                </PageHeaderDropdownMenuSection>
              )
            }

            <PageHeaderDropdownMenuSection $noBorder={isStaff || !isAdmin}>
              <PageHeaderDropdownMenuSectionTitle>
                {getText('My Account')}
              </PageHeaderDropdownMenuSectionTitle>

              <PageHeaderDropdownConditionalMenuItem
                to="/dashboard/saved-items"
                isStaff={isStaff}
                onClick={closeMenu}
              >
                <Icon
                  initialColorName="selectionGrey"
                  name="bookmark"
                  size={16}
                />
                <Box display="inline-block" ml={2}>
                  {getText('My Saved Items')}
                </Box>
              </PageHeaderDropdownConditionalMenuItem>

              {hasResumeDb(userEnvironment) ? (
                <PageHeaderDropdownConditionalMenuItem
                  qaId="page-header-account-dropdown-employment-profile"
                  to="/dashboard/employment-profile"
                  isStaff={isStaff}
                  onClick={closeMenu}
                >
                  <Icon
                    initialColorName="selectionGrey"
                    name="person"
                    size={16}
                  />
                  <Box display="inline-block" ml={2}>
                    {getText('Profile')}
                  </Box>
                </PageHeaderDropdownConditionalMenuItem>
              ) : null}

              <PageHeaderDropdownConditionalMenuItem
                qaId="page-header-account-dropdown-account-settings"
                to="/dashboard/account-settings"
                isStaff={isStaff}
                onClick={closeMenu}
              >
                <Icon initialColorName="selectionGrey" name="gear" size={16} />
                <Box display="inline-block" ml={2}>
                  {getText('Settings')}
                </Box>
              </PageHeaderDropdownConditionalMenuItem>
            </PageHeaderDropdownMenuSection>

            <PageHeaderDropdownMenuSection>
              <PageHeaderDropdownMenuItem
                href="/logout"
                data-qa-id="logout-link"
                $logoutLink
              >
                {getText('Log Out')}
              </PageHeaderDropdownMenuItem>
            </PageHeaderDropdownMenuSection>
          </>
        )}
      </PageHeaderDropdownMenu>
    </Box>
  );
}
